import { useEffect, useMemo, useRef } from "react";
import Loading from "components/shared-components/Loading";
import AppService from "services/AppService";
import { APP_PREFIX_PATH } from "configs/AppConfig";
import { useApi } from "hooks/useAPI";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import VuiBox from "components/VuiBox";
import VuiTypography from "components/VuiTypography";
import { Card, Grid } from "@mui/material";
import { useKindeAuth } from "@kinde-oss/kinde-auth-react";
import { useSnackbar } from "notistack";
import { useDispatch, useSelector } from "react-redux";
import { selectedMerchant as setSelectedMerchant } from "redux/actions";
import { useState } from "react";

const QuickbooksCallback = () => {
  const { getToken } = useApi();
  const dispatch = useDispatch();
  const searchParams = useMemo(() => {
    return new URLSearchParams(window.location.search);
  }, []);

  const { user } = useKindeAuth();
  const { enqueueSnackbar } = useSnackbar();
  const selectedMerchant = useSelector(
    (state: any) => state.merchants.selectedMerchant
  );
  const { code, state, realmId } = useMemo(() => {
    return {
      code: searchParams.get("code") || "",
      state: searchParams.get("state") || "",
      realmId: searchParams.get("realmId") || "",
    };
  }, [searchParams]);

  const hasProcessedCallback = useRef(false);

  interface QuickbooksResponse {
    id: string;
    name: string;
    quickbooksConnected: boolean;
    quickbooksTokenPeriod: object;
  }

  const redirectToStoreSettings = (delayMs: number = 0) => {
    const path = `${APP_PREFIX_PATH}/settings/store-connections`;
    if (delayMs > 0) {
      setTimeout(() => {
        window.location.href = path;
      }, delayMs);
    } else {
      window.location.href = path;
    }
  };

  useEffect(() => {
    const handleQuickbooksCallback = async () => {
      if (hasProcessedCallback.current) {
        return;
      }

      const savedState = localStorage.getItem("quickbooks_auth_state");
      if (state !== savedState) {
        enqueueSnackbar("Invalid authentication state", { variant: "error" });
        redirectToStoreSettings(3000);
        return;
      }

      if (!code) {
        enqueueSnackbar("Authorization failed", { variant: "error" });
        redirectToStoreSettings(3000);
        return;
      }

      try {
        hasProcessedCallback.current = true;
        const token = getToken ? await getToken() : "";

        const data = {
          merchantId: selectedMerchant?.id,
          authCode: code,
          realmId,
        };

        const response = await AppService.quickbooksInstall(data, token);
        const quickbooksData = response as unknown as QuickbooksResponse;

        dispatch(setSelectedMerchant({ 
          ...selectedMerchant,
          quickbooksConnected: quickbooksData?.quickbooksConnected,
          quickbooksTokenPeriod: quickbooksData?.quickbooksTokenPeriod,
        }));

        localStorage.removeItem("quickbooks_auth_state");

        enqueueSnackbar("QuickBooks connected successfully!", { 
          variant: "success" 
        });

        redirectToStoreSettings(3000);
      } catch (error) {
        enqueueSnackbar("Failed to connect QuickBooks", { variant: "error" });
        redirectToStoreSettings(3000);
      }
    };

    if (code && state) {
      handleQuickbooksCallback();
    }
  }, [code, state, realmId, getToken, enqueueSnackbar, dispatch, selectedMerchant]);

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <VuiBox py={3}>
        <Card>
          <VuiBox p={3}>
            <Grid container justifyContent="center" spacing={3}>
              <Grid item xs={12} md={8} textAlign="center">
                <VuiTypography variant="h4" color="white" fontWeight="medium">
                  Connecting to QuickBooks
                </VuiTypography>
                <VuiTypography variant="body2" color="text" mt={1}>
                  Please wait while we complete your QuickBooks connection...
                </VuiTypography>
                <VuiBox mt={4}>
                  <Loading />
                </VuiBox>
              </Grid>
            </Grid>
          </VuiBox>
        </Card>
      </VuiBox>
    </DashboardLayout>
  );
};

export default QuickbooksCallback; 